import { Injectable } from "@angular/core";
import { from, Observable } from "rxjs";
import { IGenericApiResponse } from "src/app/models/common/GenericApiResponse";
import { UserType } from "src/app/models/directory/enums/UserType.enum";
import { IDirectoryUser } from "src/app/models/directory/users/DirectoryUser";
import { GraphConstants } from "src/app/models/graph/Graph.constants";
import { IdampHttpClient } from "../common/IdampHttpClient";
import { IGenericApiResponseWithWorkflowRequest } from "src/app/models/common/GenericApiResponseWithWorkflowRequest";
import { UpdateGuestUserRequest } from "src/app/models/guestAccounts/UpdateGuestUserRequest";
import { DirectoryDomain } from "src/app/models/directory/enums/DirectoryDomain.enum";
import { UpdateServiceAccountRequest } from "src/app/models/servicePrivilegeAccounts/UpdateServiceAccountRequest";
import { CocoUserRequest } from "src/app/models/cocoUser/CocoUserRequest";
import { ICvxClaimsPrincipal } from "@cvx/cal";
import { AppRoleConstants } from "src/app/constants/AppRole.constants";
import { CreateServiceAccountRequest } from "src/app/models/servicePrivilegeAccounts/CreateServiceAccountRequest";
import { CreatePrivilegeAccountRequest } from "src/app/models/servicePrivilegeAccounts/CreatePrivilegeAccountRequest";
import { ResultSizeConstants } from "src/app/models/common/enums/ResultSizeConstants.enum";
import { UpdateCocoUserRequest } from "src/app/models/cocoUser/UpdateCocoUserRequest";
import { PersonRelationshipType } from "src/app/models/directory/enums/PersonRelationshipType.enum";
import { IBulkInviteGuestRequest } from "src/app/models/guestAccounts/IBulkInviteGuestRequest";
import { DeleteUserRequest } from "src/app/models/DeleteUserRequest";
import { DeferServiceAccountRequest } from "src/app/models/servicePrivilegeAccounts/DeferServiceAccountRequest";

@Injectable({ providedIn: 'root' })

export class GraphUserService {

    constructor(private http: IdampHttpClient) { }

    public SearchUser(domain: string, term?: string, type?: UserType, stewardedByUserId?: string, resultSize?: number, enabledAccountsOnly?: boolean, serviceId?: string): Observable<IGenericApiResponse<IDirectoryUser[]>> {
        const path = `users/${domain}`;
        let query: any = {
            searchTerm: term ?? "",
            userType: type ?? UserType.Primary,
            stewardedByUserId: stewardedByUserId ?? "",
            resultSize: resultSize ?? ResultSizeConstants.DefaultSearch,
            enabledAccountsOnly: enabledAccountsOnly ?? true,
            serviceId: serviceId ?? ""
        };

        return from(this.http.GetAsync<IGenericApiResponse<IDirectoryUser[]>>(path, query));
    }

    public DeleteUsers(deleteUserRequest: DeleteUserRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>> {

        const path = `users/${GraphConstants.DefaultGraphDomainString}/bulkDelete`;
        return from(this.http.PostAsync<DeleteUserRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>>(path, deleteUserRequest));
    }

    public UpdateGuests(updateGuestUserRequest: UpdateGuestUserRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/updateGuests`;
        return from(this.http.PatchAsync<UpdateGuestUserRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>>(path, updateGuestUserRequest));
    }

    public UnlockAccounts(domain: DirectoryDomain, accountIds: string[]): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>> {
        const path = `users/${domain}/bulkUnlock`;
        return from(this.http.PostAsync<string[], IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>>(path, accountIds));
    }

    public GetLockedAccounts(domain: DirectoryDomain, stewardedByUserId: string): Observable<IGenericApiResponse<IDirectoryUser[]>> {
        const path = `users/${domain}`;
        const query: any = { stewardedByUserId, lockedAccountsOnly: true };
        return from(this.http.GetAsync<IGenericApiResponse<IDirectoryUser[]>>(path, query));
    }

    public CreateCoco(cocoUserRequest: CocoUserRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/createCoco`;
        return from(this.http.PostAsync<CocoUserRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser>>(path, cocoUserRequest));
    }

    public CheckCocoUserSponsorshipPermission(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x === AppRoleConstants.CocoUserManageAll || x === AppRoleConstants.NonPrimaryUserManageAll)
    }

    public CheckUserGuestSponsorshipPermission(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x === AppRoleConstants.GuestUserInviteAll || x === AppRoleConstants.NonPrimaryUserManageAll) || (cvxClaimsPrincipal.userType === 'Member' && cvxClaimsPrincipal.personRelationshipType === PersonRelationshipType.Employee)
    }

    public CheckIsCocoUserAdmin(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.CocoUserManageAll);
    }

    public CheckIsNonChevronReader(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.NonChevronApplicationPermissionReadAll)
    }

    public CheckIsNonChevronManager(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.NonChevronApplicationPermissionManageAll);
    }

    public CheckIsCompromisedSourcingCompanyManager(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.CyberEventCompromisedSourcingCompanyManageAll)
    }

    public CheckIsCompromisedSourcingCompanyReader(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.CyberEventCompromisedSourcingCompanyReadAll)
    }

    public CheckIsUserNonPrimaryUserAdmin(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.NonPrimaryUserManageAll)
    }

    public CheckIsRequestReadAll(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.RequestReadAll)
    }
    public CheckIsSecretManageAll(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.SecretManageAll)
    }

    public CheckIsNonChevronWriter(cvxClaimsPrincipal: ICvxClaimsPrincipal) {
        return cvxClaimsPrincipal.roles?.some(x => x == AppRoleConstants.NonChevronApplicationPermissionManageAll);
    }

    public UpdateSevricesAccounts(updateServiceAccountRequest: UpdateServiceAccountRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/updateServiceAccounts`;
        return from(this.http.PatchAsync<UpdateServiceAccountRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>>(path, updateServiceAccountRequest));
    }

    public ResetMFA(userIds: string[]): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/bulkResetMFA`;
        return from(this.http.PostAsync<string[], IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>>(path, userIds));
    }

    public CreateServiceAccount(createServiceAccountRequest: CreateServiceAccountRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/createServiceAccount`;
        return from(this.http.PostAsync<CreateServiceAccountRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser>>(path, createServiceAccountRequest));
    }

    public CreatePrivilegeAccount(createPrivilegeAccountRequest: CreatePrivilegeAccountRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/createPrivilegeAccount`;
        return from(this.http.PostAsync<CreatePrivilegeAccountRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser>>(path, createPrivilegeAccountRequest));
    }

    public UpdateCocoUsers(updateCocoUserRequest: UpdateCocoUserRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/updateCocoUsers`;
        return from(this.http.PatchAsync<UpdateCocoUserRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>>(path, updateCocoUserRequest));
    }

    public BulkInviteGuests(bulkInviteGuestRequest: IBulkInviteGuestRequest): Observable<IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>> {
        const path = `users/${GraphConstants.DefaultGraphDomainString}/bulkInviteGuest`;
        return from(this.http.PostAsync<IBulkInviteGuestRequest, IGenericApiResponseWithWorkflowRequest<IDirectoryUser[]>>(path, bulkInviteGuestRequest));
    }

    public DeferServiceAccount(domain: string, deferRequest: DeferServiceAccountRequest): Observable<IGenericApiResponseWithWorkflowRequest<any>> {
        const path = `users/${domain}/deferServiceAccount`;
        return from(this.http.PostAsync<DeferServiceAccountRequest, IGenericApiResponseWithWorkflowRequest<any>>(path, deferRequest));
    }

    public GetCurrentIdentityInAlternativeDomain(targetDomain: string): Observable<IGenericApiResponse<IDirectoryUser>> {
        const path = `me/identity/${targetDomain}`;
        return from(this.http.GetAsync<IGenericApiResponse<IDirectoryUser>>(path));
    }

    public GetChevronIdentityInAlternativeDomain(targetDomain: string, chevronId: string): Observable<IGenericApiResponse<IDirectoryUser>> {
        const path = `users/${targetDomain}/identity/${chevronId}`;
        return from(this.http.GetAsync<IGenericApiResponse<IDirectoryUser>>(path));
    }
}